import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { glossaryService, CreateTermData } from '../../services/glossaryService';
import { CorpusSector } from '../../types';
import { Button } from '../ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

interface AddTermModalProps {
  isOpen: boolean;
  onClose: () => void;
  sectors: CorpusSector[];
  defaultSectorId?: number;
}

export default function AddTermModal({ isOpen, onClose, sectors, defaultSectorId }: AddTermModalProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<CreateTermData>({
    term: '',
    acronym: '',
    definition: '',
    simplifiedDefinition: '',
    priority: 'MEDIA',
    sectorId: defaultSectorId || (sectors[0]?.id || 0),
    mcerlLevel: 'C1',
    examples: [''],
    synonyms: [''],
  });

  useEffect(() => {
    if (defaultSectorId) {
      setFormData((prev) => ({ ...prev, sectorId: defaultSectorId }));
    }
  }, [defaultSectorId]);

  const createMutation = useMutation({
    mutationFn: (data: CreateTermData) => {
      // Filter out empty examples and synonyms
      const cleanData = {
        ...data,
        examples: data.examples?.filter((e) => e.trim() !== '') || undefined,
        synonyms: data.synonyms?.filter((s) => s.trim() !== '') || undefined,
      };
      return glossaryService.createTerm(cleanData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['glossary-terms'] });
      queryClient.invalidateQueries({ queryKey: ['glossary-stats'] });
      toast.success('Término creado exitosamente');
      resetForm();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Error al crear término');
    },
  });

  const resetForm = () => {
    setFormData({
      term: '',
      acronym: '',
      definition: '',
      simplifiedDefinition: '',
      priority: 'MEDIA',
      sectorId: defaultSectorId || (sectors[0]?.id || 0),
      mcerlLevel: 'C1',
      examples: [''],
      synonyms: [''],
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.term.trim() || !formData.definition.trim()) {
      toast.error('El término y la definición son obligatorios');
      return;
    }
    createMutation.mutate(formData);
  };

  const addExample = () => {
    setFormData((prev) => ({
      ...prev,
      examples: [...(prev.examples || []), ''],
    }));
  };

  const removeExample = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      examples: prev.examples?.filter((_, i) => i !== index),
    }));
  };

  const updateExample = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      examples: prev.examples?.map((ex, i) => (i === index ? value : ex)),
    }));
  };

  const addSynonym = () => {
    setFormData((prev) => ({
      ...prev,
      synonyms: [...(prev.synonyms || []), ''],
    }));
  };

  const removeSynonym = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      synonyms: prev.synonyms?.filter((_, i) => i !== index),
    }));
  };

  const updateSynonym = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      synonyms: prev.synonyms?.map((syn, i) => (i === index ? value : syn)),
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-blue-600 text-white p-6 rounded-t-xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Añadir Nuevo Término</h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xl" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Término <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.term}
                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Acrónimo</label>
              <input
                type="text"
                value={formData.acronym}
                onChange={(e) => setFormData({ ...formData, acronym: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Definition */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Definición <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.definition}
              onChange={(e) => setFormData({ ...formData, definition: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Simplified Definition */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Definición Simplificada
            </label>
            <textarea
              value={formData.simplifiedDefinition}
              onChange={(e) => setFormData({ ...formData, simplifiedDefinition: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Sector, Priority, Level */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Sector <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.sectorId}
                onChange={(e) => setFormData({ ...formData, sectorId: Number(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                {sectors.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.icon} {sector.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Prioridad <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value as 'ALTA' | 'MEDIA' | 'BAJA' })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="ALTA">Alta</option>
                <option value="MEDIA">Media</option>
                <option value="BAJA">Baja</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nivel MCERL <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.mcerlLevel}
                onChange={(e) =>
                  setFormData({ ...formData, mcerlLevel: e.target.value as 'B2' | 'C1' | 'C2' })
                }
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              >
                <option value="B2">B2</option>
                <option value="C1">C1</option>
                <option value="C2">C2</option>
              </select>
            </div>
          </div>

          {/* Examples */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">Ejemplos</label>
              <button
                type="button"
                onClick={addExample}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Añadir Ejemplo
              </button>
            </div>
            {formData.examples?.map((example, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={example}
                  onChange={(e) => updateExample(index, e.target.value)}
                  placeholder={`Ejemplo ${index + 1}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(formData.examples?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeExample(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Synonyms */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-gray-700">Sinónimos</label>
              <button
                type="button"
                onClick={addSynonym}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-1" />
                Añadir Sinónimo
              </button>
            </div>
            {formData.synonyms?.map((synonym, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={synonym}
                  onChange={(e) => updateSynonym(index, e.target.value)}
                  placeholder={`Sinónimo ${index + 1}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {(formData.synonyms?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSynonym(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="px-6"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="px-6 bg-blue-600 hover:bg-blue-700"
              disabled={createMutation.isPending}
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Término'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
